import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import VideoPlayer from 'components/VideoPlayer';

import Breakpoints from 'lib/Breakpoints';
import BTE from 'lib/BTE';
import {
  video as videoPropType,
} from 'lib/CustomPropTypes';
import getNavbarHeight from 'lib/getNavbarHeight';
import { stub as $t } from '@nbcnews/analytics-framework';

import './styles.themed.scss';
import { VideoPlayerContext } from 'lib/ContextTypes/videoPlayer';

const block = 'inline-video';

/**
 * InlineVideo component for rendering an inline video player with playlist and analytics.
 * @param {object} props - Component props.
 * @returns {JSX.Element|null} The rendered component.
 */
function InlineVideo(props) {
  const {
    addClassNames = '',
    autoPlay = false,
    isEmbedded = false,
    isHero = false,
    onlyAutoplayMuted = false,
    video,
    disableSticky = null,
    disablePlaylistAutoplay = false,
    isArticleVideoDateEnabled = false,
    elementId = '',
  } = props;

  const { isLiveBlog } = useContext(VideoPlayerContext) || {};
  const wrapperRef = useRef(null);
  const firedRef = useRef(false);
  const videoFocusRef = useRef(null);

  const associatedPlaylist = video?.associatedVideoPlaylist;
  const [nowPlaying, setNowPlaying] = useState(video);

  /**
   * Determines if the video is a default inline video (not embedded or hero).
   * @returns {boolean}
   */
  const isDefaultInlineVideo = !isEmbedded && !isHero;

  // registers event for inline video being fully in view
  useEffect(() => {
    if (typeof $t !== 'undefined') {
      $t('register', ['inlineVideoFullyInView']);
    }
  }, []);

  // makes it so that we fire tracking only once during the comp lifecycle, useCallback to persist
  const trackInlineVideoFullyInView = useCallback(() => {
    if (typeof $t !== 'undefined' && !firedRef.current) {
      $t('track', 'inlineVideoFullyInView');
      firedRef.current = true;
    }
  }, []);


  // debounce-bound to scroll, only calls tracking when the default inline video is fully in view.
  const monitorDefaultInlineVideos = useCallback(() => {
    const playerWrapper = wrapperRef.current;
    if (!playerWrapper) return;
    const depth = window.pageYOffset;
    const navBarHeight = getNavbarHeight(Breakpoints);
    const { top: containerTop, height: containerHeight } = playerWrapper.getBoundingClientRect();
    const topOffset = containerTop + depth;
    const bottomOffset = topOffset + containerHeight;
    const vidContainerTopShowing = topOffset > (depth + navBarHeight);
    const vidContainerBottomShowing = bottomOffset < (depth + window.innerHeight);
    if (vidContainerTopShowing && vidContainerBottomShowing) {
      trackInlineVideoFullyInView();
    }
  }, [trackInlineVideoFullyInView]);

  /**
   * Registers and cleans up the scroll event listener for tracking inline video visibility.
   *
   * if vid is the default inline video we debounce-bind monitorDefaultInlineVideos to scroll
   * which in turn triggers analytics tracking. So it's basically the last part of the scroll/fully in view tracking.
   *
   * dependencies:
   * isDefaultInlineVideo - Determines if the scroll listener should be bound.
   * monitorDefaultInlineVideos - The callback called on scroll to check visibility.
   */
  useEffect(() => {
    if (isDefaultInlineVideo) {
      BTE.on('scroll', monitorDefaultInlineVideos, 'debounce');
      return () => {
        BTE.remove('scroll', monitorDefaultInlineVideos, 'debounce');
      };
    }
    return undefined;
  }, [isDefaultInlineVideo, monitorDefaultInlineVideos]);

  const onVideoEnd = useCallback((selected) => {
    setNowPlaying(selected);
  }, []);

  if (!nowPlaying) return null;

  const embeddedStyles = classNames(
    block,
    {
      [`${block}--in-body`]: !isEmbedded && !isHero && !isLiveBlog,
    },
    addClassNames,
  );

  const isDisableSticky = disableSticky ?? (isLiveBlog && !isHero);

  const playlist = disablePlaylistAutoplay
    ? {
      ...associatedPlaylist,
      videos: [associatedPlaylist?.videos?.find((v) => v.id === nowPlaying.id)],
    }
    : associatedPlaylist;

  return (
    <section
      className={embeddedStyles}
      data-testid={`${block}`}
      id={elementId ? `anchor-${elementId}` : undefined}
    >
      <div className={`${block}__background`}>
        <div
          className={`${block}__player`}
          ref={wrapperRef}
        >
          <div ref={videoFocusRef} tabIndex={-1} />
          <VideoPlayer
            autoPlay={autoPlay}
            continuousPlay={!disablePlaylistAutoplay}
            disableSticky={isDisableSticky}
            isEmbedded={isEmbedded}
            onlyAutoplayMuted={onlyAutoplayMuted}
            onVideoEnd={onVideoEnd}
            playlist={[playlist]}
            video={nowPlaying}
            hidePublishedDate={!isArticleVideoDateEnabled}
          />
        </div>
      </div>
    </section>
  );
}

InlineVideo.propTypes = {
  addClassNames: PropTypes.string,
  autoPlay: PropTypes.bool,
  isEmbedded: PropTypes.bool,
  isHero: PropTypes.bool,
  onlyAutoplayMuted: PropTypes.bool,
  video: videoPropType.isRequired,
  disableSticky: PropTypes.bool,
  disablePlaylistAutoplay: PropTypes.bool,
  isArticleVideoDateEnabled: PropTypes.bool,
  elementId: PropTypes.string,
};
// eslint-disable-next-line
export default InlineVideo;
